import { isTokenExpired as checkTokenExpired } from '@/lib/auth';

/**
 * Token Manager Service
 * 
 * Centralizes all token-related operations including storage, retrieval, and expiry checking.
 * This creates a single source of truth for token handling across the application.
 * 
 * This implementation uses the existing auth utilities from the app.
 */

// Storage keys - using the same keys as the existing app
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

/**
 * Sets authentication tokens in localStorage with expiry calculation
 */
export const setTokens = (
  accessToken: string, 
  refreshToken: string, 
  expiresInSeconds: number = 300 // Default 5 minutes
): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  
  // Calculate expiry time (current time + expiry period - 1 min buffer)
  const expiryTime = Date.now() + (expiresInSeconds - 60) * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

/**
 * Gets the current access token from localStorage
 */
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

/**
 * Gets the current refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Checks if both access and refresh tokens exist in localStorage
 */
export const hasTokens = (): boolean => {
  return !!localStorage.getItem(ACCESS_TOKEN_KEY) && !!localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Checks if the current access token is expired
 * Uses the existing isTokenExpired function from auth.ts
 */
export const isTokenExpired = (): boolean => {
  return checkTokenExpired();
};

/**
 * Removes all authentication tokens from localStorage
 */
export const removeTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

/**
 * Updates just the access token and its expiry
 */
export const updateAccessToken = (
  accessToken: string, 
  expiresInSeconds: number = 300 // Default 5 minutes
): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  
  // Calculate expiry time (current time + expiry period - 1 min buffer)
  const expiryTime = Date.now() + (expiresInSeconds - 60) * 1000;
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

// Export the token manager as a single object for easier imports
export const tokenManager = {
  setTokens,
  getAccessToken,
  getRefreshToken, 
  hasTokens,
  isTokenExpired,
  removeTokens,
  updateAccessToken
};